import { ProductAttributeSortInput } from '@magentoTypes'
import { useRouter } from 'next/router'
import { useInfiniteQuery } from '@tanstack/react-query'
import { PAGE_SIZE } from '~/config/constants'
import { useCioClient } from '~/hooks/useCioClient'
import { mapQueryToFilters } from '../lib/mapQueryToFilters'

interface CommonProps {
  enabled: boolean
  sort: ProductAttributeSortInput
}

type CioSearchResultsProps = {
  type: 'search'
  subject: string
}

type CioBrowseResultsProps = {
  type: 'browse'
  subject: {
    filterName: string
    filterValue: string
  }
}

type Props = CommonProps & (CioSearchResultsProps | CioBrowseResultsProps)

export const useCioSearchResults = ({ type, enabled, subject }: Props) => {
  const cioClient = useCioClient()
  const { query, isReady } = useRouter()
  const filters: Record<string, any> = mapQueryToFilters(query?.filter as string)
  const sortingOption = query.sort
  const orderingOption = query.order
  const {
    data,
    isLoading,
    fetchNextPage,
    fetchPreviousPage,
    isFetching,
    isFetchingNextPage,
    isFetchingPreviousPage,
    hasNextPage,
    hasPreviousPage,
  } = useInfiniteQuery({
    queryKey: [
      'cioSearchResultProducts',
      type,
      filters,
      PAGE_SIZE,
      sortingOption,
      orderingOption,
      subject,
    ],
    queryFn: async ({ pageParam }) => {
      const params = {
        hiddenFields: [
          'parent_alternate_image',
          'parent_attribute_set_id',
          'parent_related_products_json',
          'size_id',
        ],
        filters,
        sortBy: sortingOption as string,
        sortOrder: orderingOption as string,
        page: pageParam ?? Number(query?.page ?? 1),
        resultsPerPage: PAGE_SIZE,
      }

      switch (type) {
        case 'search':
          return cioClient?.search.getSearchResults(subject, params)
        case 'browse':
          return cioClient?.browse.getBrowseResults(subject.filterName, subject.filterValue, params)
      }
    },
    getPreviousPageParam: (previousPage) => {
      if ((previousPage?.request?.page ?? 1) > 1) {
        return (previousPage?.request?.page ?? 1) - 1
      }

      return undefined
    },
    getNextPageParam: (lastPage) => {
      if (
        lastPage?.response?.total_num_results / PAGE_SIZE > 1 &&
        lastPage?.response?.results.length === PAGE_SIZE
      ) {
        return (lastPage?.request?.page ?? 1) + 1
      }

      return undefined
    },
    enabled: !!cioClient && enabled && isReady,
    keepPreviousData: false,
  })

  return {
    params: { type, subject } as CioSearchResultsProps | CioBrowseResultsProps,
    data,
    isLoading,
    isFetching,
    fetchNextPage,
    fetchPreviousPage,
    isFetchingNextPage,
    isFetchingPreviousPage,
    hasNextPage,
    hasPreviousPage,
  }
}
